import { ArrowRight, CirclePlus, ClipboardList, Info, LibraryBig } from 'lucide-react';
import { Link } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { ExerciseCard } from '@/components/exercise-card';
import { Skeleton } from '@/components/skeleton';
import { useActiveOrganization, useExerciseList, useMe, useTrainingList } from '@/lib/queries';
import { TrainingCard } from '@/components/training-card';
import { ROLE_LABEL } from '@/lib/roles';

function greeting(hour: number) {
  if (hour < 12) return 'Goedemorgen';
  if (hour < 18) return 'Goedemiddag';
  return 'Goedenavond';
}

export function DashboardPage() {
  const me = useMe();
  const { active, isPending } = useActiveOrganization();
  const recent = useExerciseList({ scope: 'mine', limit: 3 });
  const mine = recent.data?.pages[0]?.exercises ?? [];
  const trainingList = useTrainingList();
  const trainings = trainingList.data?.pages[0]?.trainings.slice(0, 3) ?? [];
  const name = me.data?.user.name ?? '';
  const firstName = name.trim().split(/\s+/)[0] ?? name;

  return (
    <div className="mx-auto grid max-w-7xl gap-6 px-4 py-5 sm:px-6 sm:py-6 lg:gap-8 lg:px-8 lg:py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid gap-1">
          <h1 className="text-h1 font-bold lg:text-h1-lg">
            {greeting(new Date().getHours())}
            {firstName && `, ${firstName}`}
          </h1>
          {active && (
            <p className="text-muted-foreground">
              {active.name} · {ROLE_LABEL[active.role]}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/trainingen/nieuw">
              <CirclePlus aria-hidden="true" />
              Nieuwe training
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/oefeningen/nieuw">Oefening toevoegen</Link>
          </Button>
        </div>
      </div>

      {!isPending && !active && (
        <Alert variant="info">
          <Info aria-hidden="true" />
          <AlertTitle>Je werkt in persoonlijke modus.</AlertTitle>
          <AlertDescription>
            <p>
              Je oefeningen blijven privé. Lid worden van een club gaat via een uitnodiging van je
              clubbeheerder.{' '}
              <Link to="/club" className="font-medium text-foreground underline underline-offset-4">
                Naar Club
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      )}

      <section aria-labelledby="mijn-trainingen" className="grid gap-3">
        <div className="flex items-center justify-between gap-2">
          <h2 id="mijn-trainingen" className="text-h3 font-semibold">
            Je trainingen
          </h2>
          {trainings.length > 0 && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/trainingen">
                Alles
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          )}
        </div>
        {trainingList.isPending ? (
          <Skeleton className="h-32 rounded-xl" />
        ) : trainings.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="Nog geen trainingen"
            description="Stel een training samen uit je oefeningen. Je ziet meteen hoe lang hij duurt."
            actions={
              <Button asChild>
                <Link to="/trainingen/nieuw">Maak je eerste training</Link>
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {trainings.map((t) => (
              <TrainingCard key={t.id} training={t} />
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="mijn-oefeningen" className="grid gap-3">
        <div className="flex items-center justify-between gap-2">
          <h2 id="mijn-oefeningen" className="text-h3 font-semibold">
            Je recente oefeningen
          </h2>
          {mine.length > 0 && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/oefeningen?scope=mine">
                Alles
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          )}
        </div>
        {recent.isPending ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : mine.length === 0 ? (
          <EmptyState
            icon={LibraryBig}
            title="Nog geen oefeningen"
            description="Schrijf een oefening uit die je vaak gebruikt. Hij blijft privé tot je hem indient."
            actions={
              <Button asChild>
                <Link to="/oefeningen/nieuw">Voeg je eerste oefening toe</Link>
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {mine.map((e) => (
              <ExerciseCard key={e.id} exercise={e} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
